"use client";

import { SignIn, SignUp, useAuth, useClerk, useSignUp, useUser } from "@clerk/nextjs";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Loader2, Mail } from "lucide-react";
import {
  clerkAuthAppearance,
  clerkSignInLocalization,
  clerkSignUpLocalization,
} from "~/lib/clerk-auth-appearance";
import {
  marketingClerkSignInAbsoluteUrl,
  marketingClerkSignUpAbsoluteUrl,
} from "~/lib/auth-paths";
import { isMarketingAuthRedirectHost } from "~/lib/marketing-brand";
import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";

type PostAiJobInlineSignupProps = {
  signInUrl: string;
  completionRedirectUrl: string;
  initialEmail?: string;
  initialFirstName?: string;
  initialLastName?: string;
  initialTitle?: string;
  initialPhone?: string;
  initialProfileImageUrl?: string;
  /** Hide optional phone field (SiteGlue onboarding Sign Up tab). */
  hidePhone?: boolean;
  onVerified: () => void;
  title?: string;
  description?: ReactNode;
  verifyingDescription?: ReactNode;
  /** Center the form in the available column (hire Preview panel). */
  centered?: boolean;
  className?: string;
};

const inlineAuthAppearance = {
  ...clerkAuthAppearance,
  elements: {
    ...clerkAuthAppearance.elements,
    rootBox: "w-full max-w-full",
    card: "shadow-none border-0 bg-transparent p-0 w-full max-w-full",
    footer: "pt-2 !flex justify-center",
    footerAction: "!flex justify-center text-sm",
    footerActionText: "text-muted-foreground",
    footerActionLink: "text-primary font-medium hover:text-primary/80",
  },
};

function resolveAppCompletionPath(completionRedirectUrl: string): string {
  try {
    const parsed = completionRedirectUrl.startsWith("http")
      ? new URL(completionRedirectUrl)
      : new URL(completionRedirectUrl, "https://app.easygpt.builders");
    return `${parsed.pathname}${parsed.search}` || "/agents/hire/custom";
  } catch {
    return "/agents/hire/custom";
  }
}

async function blobFromImageUrl(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
}

export function PostAiJobInlineSignup({
  signInUrl: _signInUrl,
  completionRedirectUrl,
  initialEmail,
  initialFirstName,
  initialLastName,
  initialTitle,
  initialPhone,
  initialProfileImageUrl,
  hidePhone = false,
  onVerified,
  title = "Sign up to deploy your agent",
  description,
  verifyingDescription,
  centered = false,
  className,
}: PostAiJobInlineSignupProps) {
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();
  const { setActive: setClerkActive } = useClerk();
  const verifiedOnce = useRef(false);
  const activatingSession = useRef(false);
  const profileApplied = useRef(false);
  const [mode, setMode] = useState<"sign-up" | "sign-in">("sign-up");
  const [onMarketingAuthHost, setOnMarketingAuthHost] = useState(false);
  const [jobTitle, setJobTitle] = useState(initialTitle ?? "");
  const [phone, setPhone] = useState(initialPhone ?? "");
  const [profilePreviewUrl, setProfilePreviewUrl] = useState(initialProfileImageUrl ?? "");
  const [profileFile, setProfileFile] = useState<File | null>(null);

  useEffect(() => {
    setOnMarketingAuthHost(isMarketingAuthRedirectHost(window.location.hostname));
  }, []);

  useEffect(() => {
    if (initialTitle) setJobTitle(initialTitle);
  }, [initialTitle]);

  useEffect(() => {
    if (initialPhone) setPhone(initialPhone);
  }, [initialPhone]);

  useEffect(() => {
    if (initialProfileImageUrl) setProfilePreviewUrl(initialProfileImageUrl);
  }, [initialProfileImageUrl]);

  // Clerk footer "Sign in" / "Sign up" links use hash URLs — keep the form in Preview.
  useEffect(() => {
    const syncModeFromHash = () => {
      const hash = window.location.hash.replace(/^#/, "");
      if (hash === "sign-in" || hash.startsWith("sign-in")) setMode("sign-in");
      if (hash === "sign-up" || hash.startsWith("sign-up")) setMode("sign-up");
    };
    syncModeFromHash();
    window.addEventListener("hashchange", syncModeFromHash);
    return () => window.removeEventListener("hashchange", syncModeFromHash);
  }, []);

  const primaryEmail = user?.primaryEmailAddress;
  const needsVerification =
    isSignedIn && primaryEmail && primaryEmail.verification.status !== "verified";

  const awaitingSignUpEmailCode =
    signUpLoaded &&
    !!signUp &&
    signUp.status === "missing_requirements" &&
    (signUp.unverifiedFields.includes("email_address") ||
      signUp.verifications.emailAddress?.status === "unverified");

  const awaitingLinkVerification = awaitingSignUpEmailCode || (isSignedIn && needsVerification);

  const verifyEmail =
    primaryEmail?.emailAddress ?? signUp?.emailAddress ?? initialEmail ?? "your inbox";

  useEffect(() => {
    if (!signUpLoaded || !signUp || activatingSession.current) return;
    if (signUp.status !== "complete" || !signUp.createdSessionId) return;

    activatingSession.current = true;
    void setClerkActive({ session: signUp.createdSessionId }).catch(() => {
      activatingSession.current = false;
    });
  }, [signUpLoaded, signUp, setClerkActive]);

  useEffect(() => {
    if (!authLoaded || !userLoaded || !user || profileApplied.current) return;
    if (!isSignedIn) return;

    const titleTrim = jobTitle.trim();
    const phoneTrim = phone.trim();
    const hasMeta = Boolean(titleTrim || phoneTrim);
    const hasImage = Boolean(profileFile || profilePreviewUrl);

    if (!hasMeta && !hasImage) return;
    profileApplied.current = true;

    void (async () => {
      try {
        if (hasMeta) {
          await user.update({
            unsafeMetadata: {
              ...(user.unsafeMetadata as Record<string, unknown>),
              ...(titleTrim ? { jobTitle: titleTrim, title: titleTrim } : {}),
              ...(phoneTrim ? { phone: phoneTrim } : {}),
            },
          });
        }
        if (profileFile) {
          await user.setProfileImage({ file: profileFile });
        } else if (profilePreviewUrl) {
          const blob = await blobFromImageUrl(profilePreviewUrl);
          if (blob) {
            await user.setProfileImage({ file: blob });
          }
        }
      } catch (err) {
        console.warn("[PostAiJobInlineSignup] profile metadata apply failed", err);
      }
    })();
  }, [
    authLoaded,
    userLoaded,
    user,
    isSignedIn,
    jobTitle,
    phone,
    profileFile,
    profilePreviewUrl,
  ]);

  useEffect(() => {
    if (!authLoaded || !userLoaded || verifiedOnce.current) return;
    if (!isSignedIn || needsVerification) return;

    verifiedOnce.current = true;
    onVerified();
  }, [authLoaded, userLoaded, isSignedIn, needsVerification, onVerified]);

  const initialValues =
    initialEmail || initialFirstName || initialLastName
      ? {
          ...(initialEmail ? { emailAddress: initialEmail } : {}),
          ...(initialFirstName ? { firstName: initialFirstName } : {}),
          ...(initialLastName ? { lastName: initialLastName } : {}),
        }
      : undefined;

  const defaultDescription = (
    <>
      Create your account so we can generate your Inbound GPT agent, show pricing, and open your
      dashboard.
    </>
  );

  const appCompletionPath = resolveAppCompletionPath(completionRedirectUrl);
  const appSignUpHref = marketingClerkSignUpAbsoluteUrl({ redirect_url: appCompletionPath });
  const appSignInHref = marketingClerkSignInAbsoluteUrl({ redirect_url: appCompletionPath });

  const profileExtras = hidePhone ? null : (
    <div className={cn("mt-4 space-y-3 text-left", centered && "mx-auto max-w-sm")}>
      <div className="space-y-1.5">
        <Label htmlFor="inline-signup-phone">Phone (optional)</Label>
        <Input
          id="inline-signup-phone"
          type="tel"
          autoComplete="tel"
          placeholder="(555) 123-4567"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-y-auto p-5",
        centered && "items-center justify-center",
        className,
      )}
    >
      <div className={cn("w-full", centered && "mx-auto max-w-md text-center")}>
        <p
          className={cn(
            "text-xs font-semibold uppercase tracking-wide text-primary",
            centered && "text-center",
          )}
        >
          {mode === "sign-in" ? "Welcome back" : "Create your account"}
        </p>
        <h2 className={cn("mt-2 text-lg font-bold", centered && "text-center")}>
          {mode === "sign-in" ? "Sign in to continue" : title}
        </h2>
        <p
          className={cn(
            "mt-2 text-sm text-muted-foreground",
            centered && "text-center",
          )}
        >
          {awaitingLinkVerification ? (
            <>
              Verify <span className="font-medium text-foreground">{verifyEmail}</span> to continue.{" "}
              {verifyingDescription ?? "We'll generate your Inbound GPT agent profile next."}
            </>
          ) : onMarketingAuthHost ? (
            mode === "sign-in"
              ? "Sign in on the secure app host to finish building your agent — then you'll return here."
              : "Create your free account on the secure app host to generate your agent preview and unlock Configure and Onboard."
          ) : mode === "sign-in" ? (
            "Sign in with your existing account to finish building your agent."
          ) : (
            (description ?? defaultDescription)
          )}
        </p>

        {awaitingLinkVerification && !awaitingSignUpEmailCode ? (
          <div className="mt-8 flex flex-col items-center gap-4 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Mail className="h-7 w-7" />
            </div>
            <p className="text-sm text-muted-foreground">Waiting for email verification…</p>
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : onMarketingAuthHost ? (
          <div className={cn("mt-6 flex flex-col gap-3", centered && "items-center")}>
            {mode === "sign-up" && profileExtras ? profileExtras : null}
            <Button asChild size="lg" className="w-full max-w-sm">
              <a href={mode === "sign-in" ? appSignInHref : appSignUpHref}>
                {mode === "sign-in" ? "Sign in to continue" : "Create free account"}
              </a>
            </Button>
            <p className="text-sm text-muted-foreground">
              {mode === "sign-up" ? (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="font-medium text-primary hover:text-primary/80"
                    onClick={() => setMode("sign-in")}
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  Don&apos;t have an account?{" "}
                  <button
                    type="button"
                    className="font-medium text-primary hover:text-primary/80"
                    onClick={() => setMode("sign-up")}
                  >
                    Sign up
                  </button>
                </>
              )}
            </p>
          </div>
        ) : (
          <div className={cn("mt-6", centered && "text-left")}>
            {mode === "sign-up" ? (
              <>
                {profileExtras}
                <div className="mt-4">
                  <SignUp
                    routing="hash"
                    // Keep Sign In inside this Preview panel (do not navigate away).
                    signInUrl="#sign-in"
                    fallbackRedirectUrl={completionRedirectUrl}
                    forceRedirectUrl={completionRedirectUrl}
                    initialValues={initialValues}
                    localization={clerkSignUpLocalization}
                    appearance={inlineAuthAppearance}
                  />
                </div>
              </>
            ) : (
              <SignIn
                routing="hash"
                signUpUrl="#sign-up"
                fallbackRedirectUrl={completionRedirectUrl}
                forceRedirectUrl={completionRedirectUrl}
                initialValues={initialEmail ? { emailAddress: initialEmail } : undefined}
                localization={clerkSignInLocalization}
                appearance={inlineAuthAppearance}
              />
            )}
          </div>
        )}

        {!awaitingLinkVerification && !onMarketingAuthHost ? (
          <p className={cn("mt-4 text-sm text-muted-foreground", centered && "text-center")}>
            {mode === "sign-up" ? (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  className="font-medium text-primary hover:text-primary/80"
                  onClick={() => setMode("sign-in")}
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don&apos;t have an account?{" "}
                <button
                  type="button"
                  className="font-medium text-primary hover:text-primary/80"
                  onClick={() => setMode("sign-up")}
                >
                  Sign up
                </button>
              </>
            )}
          </p>
        ) : null}
      </div>
    </div>
  );
}
